/**
 * @file `ChapterRole` — the flow slot a chapter fills inside its Part
 *       (umbrella #1446).
 * @module @labcraft/course-kit
 *
 * `courses/README.md` defines every Part as a four-slot flow —
 * **opening → lessons → assessment → closing**. Three of those slots are
 * chapters (opening, lesson, closing); the assessment is the Part's
 * `assessment` bank (a sibling `assessment.ts`), not a chapter. So a
 * chapter's `role` is which of the three chapter slots it fills.
 *
 * The role is authored in chapter frontmatter (`role: opening | lesson |
 * closing`) and flows through the same extract → spec → manifest path as
 * `concept`. `lintPartFlow` (see `testing/lint.ts`) enforces the flow
 * contract from the per-chapter roles; the TypeScript type stays optional
 * so a non-conforming manifest is a lint finding, not a compile error,
 * during the migration (umbrella #1446 Part A → Part F).
 *
 * The Zod schema is the single source of truth; the `ChapterRole` type is
 * derived from it (per `design.md` § "Derive types from schemas; don't
 * hand-roll parallels") so the frontmatter validator and the typed
 * surface can never drift.
 */

import { z } from 'zod'

/** Zod schema for a chapter's flow role; the source the type derives from. */
export const ChapterRoleSchema = z.enum(['opening', 'lesson', 'closing'])

/** Which slot of the Part flow a chapter fills (umbrella #1446). */
export type ChapterRole = z.infer<typeof ChapterRoleSchema>
